import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Button, Space, Tag, message, Typography, Input, Card, Tooltip, InputNumber, Modal } from 'antd';
import { SearchOutlined, ReloadOutlined, StopOutlined, CheckOutlined, GiftOutlined, AudioOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { usersApi } from '../api/client';
import { useState } from 'react';

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

interface User {
  id: string;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  isBlocked: boolean;
  freePostsLeft: number;
  audioUsageCount: number;
  lastAudioDate: string | null;
  createdAt: string;
  _count?: {
    cars: number;
  };
}

export function Users() {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [freePostsModal, setFreePostsModal] = useState<{ open: boolean; userId: string | null }>({ open: false, userId: null });
  const [freePostsCount, setFreePostsCount] = useState(1);

  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await usersApi.getAll();
      return res.data.data as User[];
    },
  });

  const resetAudioMutation = useMutation({
    mutationFn: (userId: string) => usersApi.resetAudioUsage(userId),
    onSuccess: () => {
      message.success('Audio limiti tozalandi!');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const toggleBlockMutation = useMutation({
    mutationFn: (userId: string) => usersApi.toggleBlock(userId),
    onSuccess: () => {
      message.success('Foydalanuvchi holati o\'zgartirildi!');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const grantFreePostsMutation = useMutation({
    mutationFn: ({ userId, count }: { userId: string; count: number }) => 
      usersApi.grantFreePosts(userId, count),
    onSuccess: () => {
      message.success('Bepul e\'lonlar berildi!');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setFreePostsModal({ open: false, userId: null });
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const filteredUsers = users?.filter((user) => {
    const search = searchText.toLowerCase();
    return (
      user.username?.toLowerCase().includes(search) ||
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search) ||
      user.telegramId.includes(search)
    );
  });

  const columns = [
    {
      title: 'Telegram ID',
      dataIndex: 'telegramId',
      key: 'telegramId',
      width: 140,
    },
    {
      title: 'Foydalanuvchi',
      key: 'user',
      render: (_: any, record: User) => (
        <div>
          <div>{[record.firstName, record.lastName].filter(Boolean).join(' ') || '-'}</div>
          {record.username && <small style={{ color: '#888' }}>@{record.username}</small>}
        </div>
      ),
    },
    {
      title: 'Holat',
      key: 'status',
      width: 100,
      render: (_: any, record: User) => (
        record.isBlocked ? (
          <Tag color="red">Bloklangan</Tag>
        ) : (
          <Tag color="green">Faol</Tag>
        )
      ),
    },
    {
      title: 'Audio limit',
      key: 'audioUsage',
      width: 120,
      render: (_: any, record: User) => (
        <Tooltip title={`Oxirgi ishlatish: ${record.lastAudioDate ? new Date(record.lastAudioDate).toLocaleString('uz-UZ') : '-'}`}>
          <Tag color={record.audioUsageCount >= 4 ? 'red' : 'blue'}>
            {record.audioUsageCount}/4
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Bepul e\'lon',
      dataIndex: 'freePostsLeft',
      key: 'freePostsLeft',
      width: 100,
      render: (count: number) => (
        <Tag color={count > 0 ? 'green' : 'default'}>{count}</Tag>
      ),
    },
    {
      title: 'E\'lonlar',
      key: 'carsCount',
      width: 80,
      render: (_: any, record: User) => record._count?.cars || 0,
    },
    {
      title: 'Ro\'yxatdan',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 200,
      render: (_: any, record: User) => (
        <Space size="small">
          <Tooltip title="Audio limitini tozalash">
            <Button
              icon={<AudioOutlined />}
              size="small"
              onClick={() => resetAudioMutation.mutate(record.id)}
              loading={resetAudioMutation.isPending}
              disabled={record.audioUsageCount === 0}
            />
          </Tooltip>
          <Tooltip title="Bepul e'lon berish">
            <Button
              icon={<GiftOutlined />}
              size="small"
              onClick={() => {
                setFreePostsCount(1);
                setFreePostsModal({ open: true, userId: record.id });
              }}
            />
          </Tooltip>
          <Tooltip title={record.isBlocked ? 'Blokdan chiqarish' : 'Bloklash'}>
            <Button
              icon={record.isBlocked ? <CheckOutlined /> : <StopOutlined />}
              size="small"
              danger={!record.isBlocked}
              onClick={() => toggleBlockMutation.mutate(record.id)}
              loading={toggleBlockMutation.isPending}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderRow>
        <Title level={3} style={{ margin: 0 }}>Foydalanuvchilar</Title>
        <Space>
          <Input
            placeholder="Qidirish..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Yangilash
          </Button>
        </Space>
      </HeaderRow>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="small"
        />
      </Card>

      <Modal
        title="Bepul e'lon berish"
        open={freePostsModal.open}
        onCancel={() => setFreePostsModal({ open: false, userId: null })}
        onOk={() => {
          if (freePostsModal.userId) {
            grantFreePostsMutation.mutate({ userId: freePostsModal.userId, count: freePostsCount });
          }
        }}
        confirmLoading={grantFreePostsMutation.isPending}
      >
        <div style={{ marginBottom: 16 }}>
          <label>Bepul e'lonlar soni:</label>
          <InputNumber
            min={1}
            max={100}
            value={freePostsCount}
            onChange={(v) => setFreePostsCount(v || 1)}
            style={{ width: '100%', marginTop: 8 }}
          />
        </div>
      </Modal>
    </PageContainer>
  );
}
